// KanalInspektion (I1) — Forderungen, klar & ruhig.
//
// Neubau 2026-07-06 v2 (Kevin: "Streifen + Farbe gefallen mir nicht"): weg von
// den orangen Füll-Balken. Jetzt eine ruhige Zeile je Firma: großer
// überfälliger Betrag, eine Alters-Ampel (DSO grün/amber/rot), der größte
// Schuldner rechts. Identität über einen dünnen Firmen-Farbstreifen links,
// keine flächigen Farbfüllungen. Klick öffnet die Einzelposten, der Button
// unten startet den Mahnlauf.
//
// Daten-Kontrakt: missing → "Inspektion ausstehend" · error → "Signal gestört".

import { useState } from "react";

import { formatEurCompact } from "../../lib/bwa-aggregate";
import { FIRMA_COLORS, FIRMA_LABEL, type FirmaSlug } from "../../lib/firma-colors";

export type KanalStatus = "ok" | "stale" | "missing" | "error";

export interface ZulaufDaten {
  firma: Extract<FirmaSlug, "drainx" | "gundk" | "infrax">;
  umsatzYtdEur: number | null;
  dsoDays: number | null;
  /** Offene Forderungen gesamt (brutto) — die Referenzzahl. */
  offenEur: number | null;
  /** Davon überfällig (brutto). */
  ueberfaelligEur: number | null;
  /** Größter Einzelschuldner. */
  topSchuldner: { kunde: string; betragEur: number } | null;
  /** Datenfrische der Forderungsstrecke (Snapshot-Alter). */
  frische?: "ok" | "stale" | "missing" | null;
}

interface Props {
  zulaeufe: ZulaufDaten[];
  status: KanalStatus;
  standLabel?: string | null;
  onMahnlauf: () => void;
  onPosten?: () => void;
}

function Rahmen({ hint }: { hint: string }) {
  return (
    <div>
      <div className="label-tracked mb-2">Forderungen · überfällig</div>
      <div className="flex min-h-[110px] items-center justify-center rounded-lg border border-leitstand-hairline bg-white/[0.02]">
        <span className="num-mono text-[11px] text-leitstand-dim">{hint}</span>
      </div>
    </div>
  );
}

/** Alters-Ampel nach DSO: frisch → grün, zäh → amber, alt → rot. */
function dsoAmpel(dso: number | null): { text: string; cls: string } {
  if (dso == null) return { text: "—", cls: "text-leitstand-dim" };
  const t = `${Math.round(dso)} T`;
  if (dso <= 30) return { text: t, cls: "text-emerald-300" };
  if (dso <= 55) return { text: t, cls: "text-amber-300" };
  return { text: t, cls: "text-rose-300" };
}

export default function KanalInspektion({
  zulaeufe,
  status,
  standLabel,
  onMahnlauf,
  onPosten,
}: Props) {
  const [spuelung, setSpuelung] = useState<"idle" | "frei">("idle");

  if (status === "missing") return <Rahmen hint="INSPEKTION AUSSTEHEND — Forderungen laden" />;
  if (status === "error") return <Rahmen hint="SIGNAL GESTÖRT — Forderungsstrecke gestört" />;

  const gesamtOffen = zulaeufe.reduce((s, z) => s + (z.offenEur ?? 0), 0);
  const gesamtUeberf = zulaeufe.reduce((s, z) => s + (z.ueberfaelligEur ?? 0), 0);
  const stale = status === "stale";

  const starteSpuelung = () => {
    if (spuelung !== "idle" || gesamtUeberf <= 0) return;
    setSpuelung("frei");
    onMahnlauf();
  };

  return (
    <div aria-label="Forderungen — überfällig je Firma">
      <header className="flex items-baseline justify-between gap-2">
        <div className="label-tracked">Forderungen · offen</div>
        <div className="num-mono text-sm font-semibold text-leitstand-text">
          {formatEurCompact(gesamtOffen)} offen
          <span className="text-[10px] font-normal text-signal-clay">
            {" "}
            · {formatEurCompact(gesamtUeberf)} überfällig
          </span>
          {stale && standLabel && (
            <span className="text-[10px] font-normal text-leitstand-dim"> · Stand {standLabel}</span>
          )}
        </div>
      </header>
      <p className="mb-2 text-[10px] text-leitstand-dim">
        Was uns Kunden schulden (offen) und wie viel davon überfällig ist ·
        Alter = DSO · Klick öffnet die Posten.
      </p>

      <div className="space-y-1.5">
        {zulaeufe.map((z) => {
          const palette = FIRMA_COLORS[z.firma];
          const offen = z.offenEur;
          const uf = z.ueberfaelligEur;
          const keineDaten = offen == null && uf == null;
          const kritisch = (uf ?? 0) >= 25_000;
          const ampel = dsoAmpel(z.dsoDays);
          return (
            <button
              key={z.firma}
              type="button"
              onClick={onPosten}
              disabled={!onPosten || keineDaten}
              aria-label={`${FIRMA_LABEL[z.firma]} überfällige Posten öffnen`}
              className="flex w-full items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-left hover:bg-white/[0.05] disabled:cursor-default disabled:hover:bg-white/[0.02]"
              style={{ borderLeft: `3px solid ${palette.hex}` }}
            >
              {/* Firma + Frische-Punkt */}
              <span
                className="flex w-20 shrink-0 items-center gap-1 text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: palette.hex }}
              >
                {FIRMA_LABEL[z.firma]}
                {z.frische === "stale" && (
                  <span title="Daten veraltet" className="h-1.5 w-1.5 rounded-full bg-signal-amber" />
                )}
                {z.frische === "missing" && (
                  <span title="keine aktuellen Daten" className="h-1.5 w-1.5 rounded-full bg-signal-clay" />
                )}
              </span>

              {/* Offen (Referenzzahl) + davon überfällig. */}
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-baseline gap-1.5">
                  <span
                    className={`num-mono text-lg font-bold tabular-nums ${
                      keineDaten ? "text-leitstand-dim" : "text-leitstand-text"
                    }`}
                  >
                    {keineDaten ? "—" : formatEurCompact(offen ?? 0)}
                  </span>
                  <span className="text-[10px] text-leitstand-dim">
                    {keineDaten ? "keine Forderungsdaten" : "offen"}
                  </span>
                </span>
                {!keineDaten && (
                  <span
                    className={`num-mono text-[10px] ${
                      kritisch ? "text-rose-300" : (uf ?? 0) > 0 ? "text-amber-300" : "text-emerald-300"
                    }`}
                  >
                    davon {formatEurCompact(uf ?? 0)} überfällig
                  </span>
                )}
              </span>

              {/* Alter (DSO) */}
              <span className="shrink-0 text-right">
                <span className="block text-[8px] uppercase tracking-wider text-leitstand-dim">Alter Ø</span>
                <span className={`num-mono text-[11px] ${ampel.cls}`}>{ampel.text}</span>
              </span>

              {/* Größter Schuldner */}
              <span className="hidden w-44 shrink-0 truncate text-right text-[10px] text-leitstand-dim sm:block">
                {z.topSchuldner
                  ? `größter: ${z.topSchuldner.kunde.slice(0, 18)} ${formatEurCompact(z.topSchuldner.betragEur)}`
                  : keineDaten
                    ? ""
                    : "kein Einzelposten"}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-end">
        <button
          type="button"
          onClick={starteSpuelung}
          disabled={spuelung !== "idle" || gesamtUeberf <= 0}
          className="rounded-lg border border-white/15 bg-white/5 px-3 py-1 text-[11px] text-leitstand-text hover:bg-white/10 disabled:opacity-40"
        >
          {spuelung === "frei" ? "Mahnlauf geöffnet ✓" : "Mahnlauf starten"}
        </button>
      </div>
    </div>
  );
}
